import { useReducer, useCallback, useEffect } from 'react';
import { Status } from '@restworld/utility-types';
import { useGlobalsService } from '@restworld/data-services';

enum StatusTypes {
  LOADING = 'LOADING',
  SET_STATUS_OPTIONS = 'SET_STATUS_OPTIONS',
  SET_PENDING_STATUS_OPTIONS = 'SET_PENDING_STATUS_OPTIONS',
}
export const useStatusOptions = () => {
  const globalService = useGlobalsService();
  const [statusOptionsState, dispatch] = useReducer(statusOptionReducer, {
    loading: false,
    statusOptions: {},
    pendingStatusPrefix: [],
  });
  const { statusOptions, loading, pendingStatusPrefix } = statusOptionsState;
  const fetchStatus = useCallback(
    (prefix: string[]) => {
      let needUpdate = false;
      prefix.forEach((p) => {
        if (!pendingStatusPrefix?.includes(p)) needUpdate = true;
      });
      if (!needUpdate) return;
      dispatch({
        type: StatusTypes.SET_PENDING_STATUS_OPTIONS,
        payload: {
          pendingStatusPrefix: pendingStatusPrefix
            ? [...pendingStatusPrefix, ...prefix]
            : [...prefix].filter((prefix, index, self) => self.findIndex((p) => p === prefix) === index).sort(),
        },
      });
    },
    [dispatch, pendingStatusPrefix]
  );
  const fetchStatusOptions = useCallback(
    (prefix: string[]) => {
      dispatch({ type: StatusTypes.LOADING, payload: { loading: true } });
      Promise.all(prefix.map((p) => globalService.fetchStatusOptions({ limit: 100, offset: 0, prefix: p })))
        .then((res) => {
          const newStatus: StatusOptionType = {};
          res.forEach((r, index) => {
            newStatus[prefix[index]] = r.data;
          });
          const statusOptionsCp = {
            ...statusOptions,
            ...newStatus,
          };
          dispatch({
            type: StatusTypes.SET_STATUS_OPTIONS,
            payload: {
              statusOptions: statusOptionsCp,
            },
          });
        })
        .catch((err) => {
          console.error(err, 'error fetching fetchStatusOptions');
          return err;
        })
        .finally(() => {
          dispatch({ type: StatusTypes.LOADING, payload: { loading: false } });
        });
    },
    [dispatch, globalService, statusOptions]
  );
  useEffect(() => {
    if (pendingStatusPrefix && statusOptions) {
      const usePendingOptions = pendingStatusPrefix.filter((p) => !(p in statusOptions));
      if (usePendingOptions.length) fetchStatusOptions(usePendingOptions);
    }
  }, [pendingStatusPrefix]);
  return {
    fetchStatus,
    loading,
    statusOptions,
  };
};
// --> reducer
const statusOptionReducer = (state: StatusState, action: StatusOptionAction) => {
  const { type, payload } = action;
  switch (type) {
    case StatusTypes.LOADING:
      return {
        ...state,
        loading: payload.loading,
      };
    case StatusTypes.SET_STATUS_OPTIONS:
      return {
        ...state,
        statusOptions: payload.statusOptions,
      };
    case StatusTypes.SET_PENDING_STATUS_OPTIONS:
      return {
        ...state,
        pendingStatusPrefix: payload.pendingStatusPrefix,
      };
    default:
      return { ...state };
  }
};
// --> type
interface StatusOptionAction {
  type: StatusTypes;
  payload: StatusState;
}
interface StatusState {
  loading?: boolean;
  statusOptions?: StatusOptionType;
  pendingStatusPrefix?: string[];
}
type StatusOptionType = {
  [statusName: string]: Status[];
};
